'use client';
/**
 * ReadFilterChips — the ONE thin client island on the Read surface.
 *
 * Mirrors the Newsroom's `NewsroomFilter` mechanism EXACTLY: the page stays a
 * server-rendered RSC — every card ships complete in the crawlable HTML, tagged with
 * `data-read-type="<type>"`. This island renders only the mono-uppercase chip row and
 * toggles card VISIBILITY by writing a `data-read-filter` attribute on the grid
 * container; a tiny CSS rule (emitted inline below) hides the non-matching cards. No
 * server `searchParams` read and no router round-trip → the route keeps its ISR, and a
 * deep-linked `?type=breaking` is adopted on mount and mirrored back via
 * `history.replaceState`.
 *
 * The `recaps` chip matches BOTH `daily` and `weekly` (locked decision 7). Chips carry
 * per-type counts from `ReadData.counts`.
 *
 * Chips follow the dashboard chip vocabulary: mono uppercase, gold-only accent for the
 * active state (single-accent system; chain color stays a dot elsewhere).
 */
import { useEffect, useState } from 'react';
import { READ_TYPE_LABEL, type ReadItemType } from '@/lib/read/types';

// Filter keys: 'all' + each visible type, with 'recaps' collapsing daily + weekly.
type FilterKey = 'all' | 'breaking' | 'news' | 'analysis' | 'guide' | 'recaps';

const CHIPS: { key: FilterKey; label: string; types: ReadItemType[] }[] = [
  { key: 'all', label: 'All', types: [] },
  { key: 'breaking', label: READ_TYPE_LABEL.breaking, types: ['breaking'] },
  { key: 'news', label: READ_TYPE_LABEL.news, types: ['news'] },
  { key: 'analysis', label: READ_TYPE_LABEL.analysis, types: ['analysis'] },
  { key: 'guide', label: 'Guides', types: ['guide'] },
  { key: 'recaps', label: 'Recaps', types: ['daily', 'weekly'] },
];

const isFilterKey = (v: string): v is FilterKey =>
  CHIPS.some((c) => c.key === v);

/** The container that wraps the cards must carry this id so the CSS rule can scope. */
export const READ_GRID_ID = 'read-grid';

export interface ReadFilterChipsProps {
  /** Per-type counts (from `ReadData.counts`) shown as a chip suffix. */
  counts: Record<ReadItemType, number>;
}

export default function ReadFilterChips({ counts }: ReadFilterChipsProps) {
  const [filter, setFilter] = useState<FilterKey>('all');

  // Adopt ?type= from the URL once on mount (deep-linkable). The server always renders
  // "all" (no hidden cards), so crawlers see every item.
  useEffect(() => {
    const param = new URLSearchParams(window.location.search).get('type');
    if (param && isFilterKey(param)) {
      setFilter(param);
      applyFilter(param);
    }
  }, []);

  const total = Object.values(counts).reduce((s, n) => s + n, 0);
  const countFor = (chip: (typeof CHIPS)[number]): number =>
    chip.key === 'all'
      ? total
      : chip.types.reduce((s, t) => s + (counts[t] ?? 0), 0);

  const select = (key: FilterKey) => {
    setFilter(key);
    applyFilter(key);
    const url = new URL(window.location.href);
    if (key === 'all') url.searchParams.delete('type');
    else url.searchParams.set('type', key);
    window.history.replaceState(null, '', url.toString());
  };

  return (
    <>
      {/* Visibility CSS — scoped to the grid by its data-read-filter value. When the
          filter is anything but "all", every card whose data-read-type isn't part of
          the active set is hidden (content stays in the DOM). The 'recaps' filter
          matches both daily + weekly. */}
      {/* dangerouslySetInnerHTML is required: React HTML-escapes text children of
          <style> during SSR (`>` → `&gt;`), which both breaks the served CSS and
          causes a hydration text mismatch. */}
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      {/* Sticky filter bar: pins under the 56px TopBar as the feed scrolls so the
          content-type filter is always in reach (spec §B discovery affordance). Spans
          the full content width (no negative margins) so feed cards scroll cleanly under
          its blurred backdrop. z-40 stays below the TopBar (z-50). No layout shift: the
          bar stays in normal flow; sticky only repaints its position once scrolled. */}
      <div
        role="group"
        aria-label="Filter the Read feed"
        className="sticky top-[56px] z-40 mb-[26px] flex flex-wrap gap-1.5 border-b border-divider bg-ink/85 py-2.5 backdrop-blur-md"
      >
        {CHIPS.map((chip) => {
          const active = filter === chip.key;
          const n = countFor(chip);
          // Hide a chip (other than All) that has zero matching items.
          if (chip.key !== 'all' && n === 0) return null;
          return (
            <button
              key={chip.key}
              type="button"
              aria-pressed={active}
              onClick={() => select(chip.key)}
              className={`inline-flex items-center gap-1.5 rounded border px-3 py-1.5 font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] transition-colors ${
                active
                  ? 'border-scout-gold/40 bg-scout-gold-dim text-scout-gold'
                  : 'border-divider bg-surface text-parchment-dim hover:border-scout-gold/30 hover:text-parchment'
              }`}
            >
              {chip.label}
              <span className={active ? 'text-scout-gold/70' : 'text-parchment-dim/70'}>
                {n}
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
}

/** Imperatively stamp the active filter onto the grid container (no React re-render of
 *  the server-rendered cards — they stay put; only their visibility flips via CSS). */
function applyFilter(key: FilterKey) {
  if (typeof document === 'undefined') return;
  const grid = document.getElementById(READ_GRID_ID);
  if (!grid) return;
  if (key === 'all') grid.removeAttribute('data-read-filter');
  else grid.setAttribute('data-read-filter', key);
}

// CSS-visibility rules: when the grid carries data-read-filter="<key>", hide every
// card whose data-read-type is NOT in that key's set. 'recaps' keeps daily + weekly.
const CSS = `
#${READ_GRID_ID}[data-read-filter] > [data-read-type] { display: none; }
#${READ_GRID_ID}[data-read-filter="breaking"] > [data-read-type="breaking"],
#${READ_GRID_ID}[data-read-filter="news"] > [data-read-type="news"],
#${READ_GRID_ID}[data-read-filter="analysis"] > [data-read-type="analysis"],
#${READ_GRID_ID}[data-read-filter="guide"] > [data-read-type="guide"],
#${READ_GRID_ID}[data-read-filter="recaps"] > [data-read-type="daily"],
#${READ_GRID_ID}[data-read-filter="recaps"] > [data-read-type="weekly"] { display: flex; }
`;
